import type { DownloadStatsDTO } from '../types/assignments.types';

interface DownloadStatsPanelProps {
  stats: DownloadStatsDTO | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

function StatCell({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="py-3 px-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

type ReasonEntries = Array<[string, number]>;
type ReasonSource = DownloadStatsDTO['failure_reasons'];

function normalizeReasonEntries(reasons: ReasonSource): ReasonEntries {
  if (!reasons) return [];

  if (Array.isArray(reasons)) {
    return reasons.map((item) => [
      item.code || 'unknown',
      Number.isFinite(item.count) ? item.count : 0,
    ]);
  }

  return Object.entries(reasons).map(([code, count]) => [
    code || 'unknown',
    Number.isFinite(count) ? count : 0,
  ]);
}

export function DownloadStatsPanel({ stats, loading, error, onRetry }: DownloadStatsPanelProps) {
  if (loading) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-medium text-gray-900">任务统计</h3>
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-medium text-gray-900">任务统计</h3>
        <div className="py-6 text-center">
          <p className="text-sm text-red-500 mb-2">{error}</p>
          <button onClick={onRetry} className="text-sm text-primary hover:underline">重试</button>
        </div>
      </section>
    );
  }

  if (!stats) return null;

  const successRatePct = (stats.success_rate * 100).toFixed(1);
  const failureEntries = normalizeReasonEntries(stats.failure_reasons);
  const riskEntries = normalizeReasonEntries(stats.risk_reasons);

  return (
    <section className="space-y-3">
      <h3 className="text-base font-medium text-gray-900">任务统计</h3>

      <div className="grid grid-cols-5 border border-gray-200 rounded-md divide-x divide-gray-200">
        <StatCell label="总尝试" value={stats.total_attempts} />
        <StatCell label="成功" value={stats.success_count} sub={`${successRatePct}%`} />
        <StatCell label="待人工审核" value={stats.pending_review_count} />
        <StatCell label="风控待审" value={stats.risk_review_required_count} />
        <StatCell
          label="失败原因数"
          value={failureEntries.length}
          sub={failureEntries.length > 0 ? `共 ${failureEntries.reduce((s, [, c]) => s + c, 0)} 次` : undefined}
        />
      </div>

      {failureEntries.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500">失败原因</p>
          <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
            {failureEntries.map(([reason, count]) => (
              <div key={reason} className="flex items-center justify-between px-4 py-2">
                <span className="text-sm text-gray-700">{reason}</span>
                <span className="text-sm text-gray-900 tabular-nums font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {riskEntries.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500">风控原因</p>
          <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
            {riskEntries.map(([reason, count]) => (
              <div key={reason} className="flex items-center justify-between px-4 py-2">
                <span className="text-sm text-gray-700">{reason}</span>
                <span className="text-sm text-gray-900 tabular-nums font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
